import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { FamilyTreeNode } from '../components/FamilyTreeNode';
import { useFamilyNames, useUsers } from '../hooks/useSharedData';
import { familyTreeService } from '../services/api';
import {
  buildFamilyTree,
  countTreeNodes,
  getVisibleFamilyTreeUsers,
  getDescendantFamilyTreeUsers,
  UserRecord,
  UserTreeNode,
} from '../utils/familyTreeHelper';
import '../styles/family-tree.scss';
import '../styles/layout.scss';

export const FamilyTreePage: React.FC = () => {
  const location = useLocation();
  const isShareView = location.pathname === '/family-tree/share';
  const shareParams = new URLSearchParams(location.search);
  const sharedFamilyId = Number(shareParams.get('familyNameId'));
  const shareToken = shareParams.get('token') || '';
  const [treeRoots, setTreeRoots] = useState<UserTreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [selectedRootUserId, setSelectedRootUserId] = useState<string>('');
  const [sharedUsers, setSharedUsers] = useState<UserRecord[]>([]);
  const [sharedHeadName, setSharedHeadName] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const { user } = useAuth();
  const { toggleSidebar } = useSidebar();
  const { data: familyNames, loading: familyNamesLoading } = useFamilyNames();
  const { data: users, loading: usersLoading, error: usersError } = useUsers();

  useEffect(() => {
    if (!isShareView) {
      return;
    }

    if (!sharedFamilyId || !shareToken) {
      setError('Link chia sẻ không hợp lệ.');
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError('');
    familyTreeService.getSharedTree(sharedFamilyId, shareToken)
      .then((response) => {
        if (!cancelled) {
          setSharedUsers(response?.data?.users ?? []);
          setSharedHeadName(response?.data?.headUserName || '');
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Không thể tải cây phả hệ từ link chia sẻ');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isShareView, sharedFamilyId, shareToken]);

  useEffect(() => {
    if (isShareView) {
      if (!isLoading) {
        const visibleUsers = getVisibleFamilyTreeUsers(
          { familyNameId: sharedFamilyId },
          sharedUsers,
          sharedFamilyId
        );
        setTreeRoots(buildFamilyTree(visibleUsers, sharedUsers));
      }
      return;
    }

    if (usersLoading || familyNamesLoading) {
      setIsLoading(true);
      setError(usersError || '');
      return;
    }

    try {
      setError(usersError || '');

      const allUsers = users as UserRecord[];
      if (user?.isAdmin && !selectedFamilyId) {
        setTreeRoots([]);
        return;
      }

      const effectiveFamilyId = user?.isAdmin ? selectedFamilyId : String(user?.familyNameId || '');
      const visibleUsers = effectiveFamilyId
        ? getVisibleFamilyTreeUsers(user, allUsers, effectiveFamilyId)
        : [];
      const filteredUsers = selectedRootUserId
        ? getDescendantFamilyTreeUsers(visibleUsers, Number(selectedRootUserId))
        : visibleUsers;
      setTreeRoots(buildFamilyTree(filteredUsers, allUsers));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Không thể tải cây phả hệ');
    }
    setIsLoading(false);
  }, [isShareView, isLoading, sharedFamilyId, sharedUsers, selectedFamilyId, selectedRootUserId, user, user?.id, user?.isAdmin, user?.familyNameId, users, usersLoading, familyNamesLoading, usersError]);

  const handleShare = async () => {
    const familyId = user?.isAdmin ? Number(selectedFamilyId) : user?.familyNameId;
    if (!familyId) {
      setError('Vui lòng chọn dòng họ trước khi chia sẻ.');
      return;
    }

    setIsCreatingShare(true);
    setShareMessage('');
    try {
      const response = await familyTreeService.createShareToken(familyId);
      const token = response?.data?.token;
      if (!token) {
        throw new Error('Không nhận được mã chia sẻ');
      }

      const shareUrl = `${window.location.origin}/family-tree/share?familyNameId=${familyId}&token=${encodeURIComponent(token)}`;
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage('Đã tạo và sao chép link chia sẻ. Link có hiệu lực trong 30 ngày.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Không thể tạo link chia sẻ');
    } finally {
      setIsCreatingShare(false);
    }
  };

  const memberCount = countTreeNodes(treeRoots);
  const activeFamilyId = user?.isAdmin ? Number(selectedFamilyId) : user?.familyNameId;
  const activeFamily = familyNames.find((family: any) => family.id === activeFamilyId);
  const activeFamilyHeadName = isShareView ? sharedHeadName : activeFamily?.headUserName;
  const familyMembers = (users as UserRecord[]).filter(
    (candidate) =>
      !candidate.isAdmin &&
      candidate.familyNameId === activeFamilyId &&
      candidate.gender?.toLowerCase() === 'male'
  ).sort((first, second) => {
    const generationDifference = (first.generation ?? 1) - (second.generation ?? 1);
    if (generationDifference !== 0) return generationDifference;
    return (first.birthYear ?? Number.MAX_SAFE_INTEGER) - (second.birthYear ?? Number.MAX_SAFE_INTEGER);
  });

  return (
    <div className={`main-layout family-tree-page${isShareView ? ' sidebar-hidden' : ''}`}>
      {!isShareView && <Sidebar />}
      <div className="main-content">
        <div className="header">
          {!isShareView && (
            <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Toggle sidebar">
              <i className="fas fa-bars"></i>
            </button>
          )}
          <h1>
            <i className="fas fa-sitemap"></i> {isShareView ? 'Cây phả hệ được chia sẻ' : 'Cây phả hệ'}
          </h1>
        </div>

        {!isShareView && (
          <div className="family-tree-toolbar">
            {user?.isAdmin && (
              <>
                <label htmlFor="family-select">Chọn dòng họ</label>
                <div className="family-tree-select-wrap">
                  <i className="fas fa-filter"></i>
                  <select
                    id="family-select"
                    value={selectedFamilyId}
                    onChange={(event) => {
                      setSelectedFamilyId(event.target.value);
                      setSelectedRootUserId('');
                    }}
                  >
                    <option value="">Chọn dòng họ</option>
                    {familyNames.map((family) => (
                      <option key={family.id} value={family.id}>
                        {family.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <label htmlFor="tree-root-select">Hiển thị từ</label>
            <div className="family-tree-select-wrap">
              <i className="fas fa-sitemap"></i>
              <select
                id="tree-root-select"
                value={selectedRootUserId}
                onChange={(event) => setSelectedRootUserId(event.target.value)}
              >
                <option value="">Toàn bộ cây</option>
                {familyMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.fullName}{member.homeName ? ` - ${member.homeName}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" onClick={handleShare} disabled={isCreatingShare}>
              <i className="fas fa-share-alt"></i> {isCreatingShare ? 'Đang tạo...' : 'Chia sẻ'}
            </button>
            {shareMessage && <span className="share-message">{shareMessage}</span>}
          </div>
        )}

        <div className="content-area">
          {error && <div className="alert alert-danger">{error}</div>}
          <div className="family-tree-container">
            {isLoading ? (
              <div className="family-tree-loading" role="status" aria-live="polite">
                <div className="family-tree-loading__intro">
                  <div className="spinner"></div>
                  <div>
                    <strong>Đang dựng cây phả hệ</strong>
                    <span>Đang tải thông tin thành viên...</span>
                  </div>
                </div>
                <div className="family-tree-loading__stats">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="family-tree-loading__branches">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            ) : memberCount === 0 ? (
              <div className="empty-state">
                <i className="fas fa-tree"></i>
                <p>
                  {!isShareView && user?.isAdmin && !selectedFamilyId
                    ? 'Vui lòng chọn dòng họ để hiển thị cây phả hệ.'
                    : 'Chưa có thành viên nào trong cây phả hệ.'}
                </p>
              </div>
            ) : (
              <>
                <div className="family-tree-stats">
                  <span><strong>{memberCount}</strong> thành viên</span>
                  <span><strong>{treeRoots.length}</strong> nhánh gốc</span>
                  <span><strong>Trưởng họ:</strong> {activeFamilyHeadName || 'Chưa chọn'}</span>
                </div>
                <div className="family-tree">
                  <ul>
                    {treeRoots.map((root) => <FamilyTreeNode key={root.id} node={root} />)}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
